import React, { Component } from "react";
import { graphql, withApollo } from "react-apollo";
import gql from "graphql-tag";
import compose from "lodash.flowright";
import { MathJax, MathJaxContext } from "better-react-mathjax";

/* Records how long the student spent on a topic and whether they completed it. */
const SAVE_NOTES_PROGRESS = gql`
  mutation ($registration_id: Int!, $topic_id: Int!, $chapter_id: Int, $seconds: Int, $completed: Boolean) {
    saveFoundationNotesProgress(registration_id: $registration_id, topic_id: $topic_id, chapter_id: $chapter_id, seconds: $seconds, completed: $completed)
  }
`;

/* ============================================================================
   IIT Foundation Short Notes — live data.

   Query: getIITFoundationShortNotes(subject, class_id) → chapters → topics.
   Every section renders from the API response; there is no fixture data.

   Chapters are keyed to f_chapter.class_id → f_class.id, which is the same id
   the logged-in student carries as grade_id — so the page asks for its notes by
   id rather than by a class string. `class` comes back for the "Class 8"
   captions, and the legacy $class variable is sent only when no class_id is
   known (someone opening the page without a session).

   The project ships Bootstrap 4.4 and Font Awesome Pro 5.11 (loaded globally
   in App.js) and has no `react-icons`, so icons are FA classes and the
   Bootstrap-5 look is the scoped CSS below. No new packages are required.
   ========================================================================== */

export const GET_IIT_FOUNDATION_SHORT_NOTES = gql`
  query ($subject: String, $class_id: Int, $class: String) {
    getIITFoundationShortNotes(subject: $subject, class_id: $class_id, class: $class) {
      id
      chapterName
      class_id
      class_label
      class
      subject
      topics {
        id
        name
        readingTime
        difficulty
        quickSummary
        keyPoints {
          id
          icon
          title
          text
        }
        formulae {
          id
          title
          formula
          note
          tone
        }
        importantNotes {
          id
          title
          text
        }
        commonMistakes {
          id
          title
          text
        }
      }
    }
  }
`;

/* Filter options — UI configuration, not content. */
const SUBJECT_OPTIONS = ["Mathematics", "Physics", "Chemistry", "Biology"];

/* The API sends plain icon names; map them onto the Font Awesome set. */
const ICON_MAP = {
  "book-open": "fas fa-book-open",
  lightbulb: "fas fa-lightbulb",
  settings: "fas fa-cog",
  globe: "fas fa-globe",
  rocket: "fas fa-rocket",
  target: "fas fa-bullseye",
  star: "fas fa-star",
  check: "fas fa-check-circle",
};
const iconFor = (name) => ICON_MAP[name] || "fas fa-circle";

/* Difficulty and formula tones arrive as free text — normalise them. */
const difficultyTone = (value) => {
  const key = String(value || "").toLowerCase();
  if (key === "easy") return "green";
  if (key === "hard") return "red";
  if (key === "medium") return "amber";
  return "blue";
};

const DOT_COLOR = { green: "var(--fdn-green)", amber: "var(--fdn-yellow)", red: "var(--fdn-red)", blue: "var(--fdn-blue)" };

/* Presentation only: subject colour class (theme/_fdn-theme.scss) and icon. */
const subjectClass = (name) => `fdn-subj--${String(name || "").toLowerCase()}`;
const SUBJECT_ICON = {
  Physics: "fas fa-atom",
  Chemistry: "fas fa-flask",
  Mathematics: "fas fa-square-root-alt",
  Biology: "fas fa-dna",
  Science: "fas fa-microscope",
};

const TONE_MAP = {
  warning: "amber",
  danger: "red",
  success: "green",
  info: "blue",
  primary: "purple",
  secondary: "blue",
};
const formulaTone = (value) => TONE_MAP[String(value || "").toLowerCase()] || "blue";

const MATHJAX_CONFIG = {
  loader: { load: ["input/mml", "output/chtml"] },
  options: { enableMenu: false },
};

/* ------------------------------------------------------------------ styles */
const STYLES = `
/* Built on the foundation design system (theme/_fdn-theme.scss): the page
   colour is pink, subject UI follows --subj, everything else uses --fdn-* tokens. */
.snx { --snx-accent:var(--fdn-pink); --snx-accent-dark:var(--fdn-pink-dark); --snx-accent-soft:var(--fdn-pink-soft);
  max-width:1360px; margin:0 auto; min-height:100%; color:var(--fdn-ink); font-size:15px; }
.snx h5, .snx h6 { font-family:var(--fdn-display); letter-spacing:-.1px; }

/* header (fdn-hero fdn-hero--pink) */
.snx .fdn-hero__sub { max-width:640px; }

/* filters - sit on the hero gradient */
.snx-filters { display:grid; gap:12px; grid-template-columns:minmax(0,2fr) minmax(0,1fr); }
.snx-field { position:relative; }
.snx-field > i { position:absolute; left:16px; top:50%; transform:translateY(-50%); font-size:14px;
  color:var(--fdn-muted); pointer-events:none; }
.snx-field input, .snx-field select { width:100%; height:50px; padding:0 16px 0 44px; border:2px solid transparent;
  border-radius:var(--fdn-radius); background:var(--fdn-surface); font-size:15px; font-weight:700;
  color:var(--fdn-ink); appearance:none; -webkit-appearance:none; box-shadow:0 6px 0 rgba(29,27,58,.12);
  transition:border-color .2s ease, box-shadow .2s ease; }
.snx-field input::placeholder { color:var(--fdn-muted); font-weight:600; }
.snx-field input:focus, .snx-field select:focus { outline:none; border-color:var(--fdn-yellow);
  box-shadow:0 6px 0 rgba(29,27,58,.12), 0 0 0 4px rgba(255,255,255,.35); }
.snx-field--subject select { padding-left:58px; padding-right:42px; cursor:pointer; color:var(--subj-dark); }
.snx-field--subject .snx-field__badge { position:absolute; left:9px; top:50%; transform:translateY(-50%) rotate(-6deg);
  display:flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:12px;
  font-size:15px; color:#fff; background:var(--subj); box-shadow:0 4px 10px -3px var(--subj); pointer-events:none; }
.snx-field--subject .snx-field__caret { position:absolute; right:16px; top:50%; transform:translateY(-50%);
  font-size:12px; color:var(--subj-dark); pointer-events:none; }

/* layout */
.snx-grid { display:grid; grid-template-columns:300px minmax(0,1fr); gap:22px; align-items:start; }

/* sidebar - chapter / topic navigator */
.snx-side { position:sticky; top:16px; max-height:calc(100vh - 32px); overflow-y:auto; padding:18px 14px;
  border-radius:var(--fdn-radius-lg); background:var(--fdn-surface); border:1.5px solid var(--fdn-line);
  box-shadow:var(--fdn-shadow-sm); }
.snx-side::-webkit-scrollbar { width:6px; }
.snx-side::-webkit-scrollbar-thumb { background:var(--fdn-line-strong); border-radius:6px; }
.snx-side__title { display:flex; align-items:center; gap:9px; margin:0 4px 14px; font-size:16px; font-weight:600;
  color:var(--fdn-ink); }
.snx-side__title i { display:flex; align-items:center; justify-content:center; width:30px; height:30px;
  border-radius:10px; font-size:13px; color:var(--snx-accent); background:var(--snx-accent-soft); }
.snx-chapterbox { margin-bottom:10px; border-radius:var(--fdn-radius); border:2px solid var(--fdn-line);
  background:var(--fdn-surface); transition:border-color .2s ease, box-shadow .2s ease; }
.snx-chapterbox.is-open { border-color:var(--snx-accent-soft); box-shadow:0 8px 18px -10px var(--snx-accent); }
.snx-chapterbox__btn { display:flex; align-items:center; gap:10px; width:100%; padding:11px 12px; border:0;
  border-radius:14px; background:transparent; font-size:14.5px; font-weight:800; line-height:1.3;
  color:var(--fdn-ink); cursor:pointer; text-align:left; transition:background .2s ease, color .2s ease; }
.snx-chapterbox__btn:focus { outline:none; }
.snx-chapterbox__btn:focus-visible { box-shadow:0 0 0 3px var(--snx-accent-soft); }
.snx-chapterbox__btn:hover { background:var(--fdn-bg); }
.snx-chapterbox.is-open .snx-chapterbox__btn { background:var(--snx-accent-soft); color:var(--snx-accent-dark);
  border-radius:14px 14px 0 0; }
.snx-chapterbox__btn .ic { display:flex; align-items:center; justify-content:center; width:32px; height:32px;
  border-radius:11px; font-size:13px; color:var(--snx-accent); background:var(--snx-accent-soft); flex:0 0 auto;
  transition:transform .35s var(--fdn-bounce), background .2s ease, color .2s ease; }
.snx-chapterbox__btn:hover .ic { transform:rotate(-8deg) scale(1.06); }
.snx-chapterbox.is-open .snx-chapterbox__btn .ic { color:#fff; background:var(--snx-accent); }
.snx-chapterbox__btn .caret { margin-left:auto; font-size:11px; color:var(--fdn-muted); }
.snx-topics { padding:8px; }
.snx-topic { display:flex; align-items:center; gap:10px; width:100%; padding:10px 12px; margin-bottom:4px;
  border:0; border-radius:var(--fdn-radius-sm); background:transparent; font-size:14px; font-weight:700;
  line-height:1.35; color:var(--fdn-body); text-align:left; cursor:pointer; transition:all .22s var(--fdn-ease); }
.snx-topic:last-child { margin-bottom:0; }
.snx-topic:focus { outline:none; }
.snx-topic:focus-visible { box-shadow:0 0 0 3px var(--snx-accent-soft); }
.snx-topic:hover { background:var(--fdn-bg); color:var(--snx-accent-dark); transform:translateX(3px); }
.snx-topic.is-active { color:#fff; background:var(--snx-accent); box-shadow:0 4px 0 var(--snx-accent-dark); }
.snx-topic.is-active:hover { transform:none; }
.snx-topic .dot { width:10px; height:10px; border-radius:50%; flex:0 0 auto; box-shadow:0 0 0 3px var(--fdn-bg); }
.snx-topic.is-active .dot { box-shadow:0 0 0 3px rgba(255,255,255,.55); }
.snx-topic small { margin-left:auto; padding:2px 8px; border-radius:var(--fdn-radius-pill); font-size:11px;
  font-weight:800; white-space:nowrap; color:var(--fdn-muted); background:var(--fdn-bg); }
.snx-topic.is-active small { color:#fff; background:rgba(255,255,255,.22); }

/* main */
.snx-main { min-width:0; }
.snx-card { padding:22px 24px; margin-bottom:18px; border-radius:var(--fdn-radius-lg); background:var(--fdn-surface);
  border:1.5px solid var(--fdn-line); box-shadow:var(--fdn-shadow-sm);
  transition:transform .3s var(--fdn-ease), box-shadow .3s ease, border-color .3s ease; }
.snx-card:hover { transform:translateY(-3px); box-shadow:var(--fdn-shadow); border-color:var(--fdn-line-strong); }
.snx-card__head { display:flex; align-items:center; gap:12px; margin-bottom:18px; }
.snx-card__head .ic { display:flex; align-items:center; justify-content:center; width:42px; height:42px;
  border-radius:14px; font-size:17px; color:#fff; flex:0 0 auto; transform:rotate(-6deg);
  box-shadow:0 6px 14px -6px rgba(29,27,58,.4); transition:transform .4s var(--fdn-bounce); }
.snx-card:hover .snx-card__head .ic { transform:rotate(4deg) scale(1.05); }
.snx-card__head h5 { margin:0; font-size:19px; font-weight:600; color:var(--fdn-ink); }
.snx-card__head small { display:block; font-size:13px; font-weight:700; color:var(--fdn-muted); }

/* topic header - tinted by subject */
.snx-hero { position:relative; overflow:hidden; padding:24px 26px; margin-bottom:18px; border-radius:var(--fdn-radius-lg);
  background:var(--subj-soft); border:2px solid var(--fdn-surface); box-shadow:var(--fdn-shadow-sm); }
.snx-hero:after { content:""; position:absolute; width:150px; height:150px; right:-40px; top:-60px; border-radius:50%;
  background:var(--subj); opacity:.12; pointer-events:none; }
.snx-hero > * { position:relative; z-index:1; }
.snx-hero__crumb { margin-bottom:8px; font-size:13px;
  font-weight:800; color:var(--subj-dark); }
.snx-hero__title { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; flex-wrap:wrap; }
.snx-hero h2 { margin:0 0 14px; font-size:28px; font-weight:700; line-height:1.2; color:var(--fdn-ink); }
.snx-complete { display:inline-flex; align-items:center; gap:8px; padding:11px 20px; border:0;
  border-radius:var(--fdn-radius-sm); background:var(--snx-accent); color:#fff; font-size:14px; font-weight:800;
  line-height:1.2; cursor:pointer; box-shadow:0 4px 0 var(--snx-accent-dark);
  transition:transform .12s ease, box-shadow .12s ease, filter .2s ease; white-space:nowrap; flex:0 0 auto; }
.snx-complete i { font-size:15px; }
.snx-complete:focus { outline:none; }
.snx-complete:hover:not(:disabled) { filter:brightness(1.05); transform:translateY(-1px); box-shadow:0 5px 0 var(--snx-accent-dark); }
.snx-complete:active:not(:disabled) { transform:translateY(3px); box-shadow:0 1px 0 var(--snx-accent-dark); }
.snx-complete.is-done { background:var(--fdn-green-soft); color:var(--fdn-green-dark); box-shadow:0 4px 0 var(--fdn-green);
  cursor:default; }
.snx-hero__meta { display:flex; flex-wrap:wrap; gap:8px; }
.snx-meta { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:var(--fdn-radius-pill);
  font-size:13px; font-weight:800; background:var(--fdn-surface); color:var(--fdn-body); }
.snx-meta i { color:var(--subj); }
.snx-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:var(--fdn-radius-pill);
  font-size:13px; font-weight:800; text-transform:capitalize; }
.snx-badge.green { color:var(--fdn-green-dark); background:var(--fdn-green-soft); }
.snx-badge.amber { color:var(--fdn-orange-dark); background:var(--fdn-yellow-soft); }
.snx-badge.red { color:var(--fdn-red-dark); background:var(--fdn-red-soft); }
.snx-badge.blue { color:var(--fdn-blue-dark); background:var(--fdn-blue-soft); }

/* quick summary */
.snx-summary { position:relative; padding:22px 24px 22px 26px; margin-bottom:18px; border-radius:var(--fdn-radius-lg);
  background:var(--fdn-yellow-soft); border:2px dashed var(--fdn-yellow); }
.snx-summary h5 { display:flex; align-items:center; gap:10px; margin:0 0 10px; font-size:19px; font-weight:600;
  color:var(--fdn-ink); }
.snx-summary h5 i { display:flex; align-items:center; justify-content:center; width:34px; height:34px;
  border-radius:11px; font-size:15px; color:var(--fdn-ink); background:var(--fdn-yellow); transform:rotate(-6deg); }
.snx-summary p { max-width:72ch; margin:0; font-size:16px; line-height:1.75; color:var(--fdn-body); }

/* key points */
.snx-points { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:14px; }
.snx-point { padding:18px; border-radius:var(--fdn-radius); background:var(--fdn-blue-soft); border:2px solid transparent;
  transition:all .26s var(--fdn-ease); }
.snx-point:hover { background:var(--fdn-surface); border-color:var(--fdn-blue); transform:translateY(-4px);
  box-shadow:var(--fdn-shadow); }
.snx-point .ic { display:flex; align-items:center; justify-content:center; width:38px; height:38px;
  margin-bottom:12px; border-radius:12px; font-size:15px; color:#fff; background:var(--fdn-blue);
  box-shadow:0 4px 0 var(--fdn-blue-dark); }
.snx-point h6 { margin:0 0 6px; font-size:16px; font-weight:600; color:var(--fdn-ink); }
.snx-point p { margin:0; font-size:14.5px; line-height:1.65; color:var(--fdn-body); }

/* formulae */
.snx-formulae { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; }
.snx-formula { --fm:var(--fdn-blue); --fm-soft:var(--fdn-blue-soft); padding:20px 18px; border-radius:var(--fdn-radius);
  text-align:center; background:var(--fm-soft); border:2px solid transparent; transition:all .28s var(--fdn-ease); }
.snx-formula.purple { --fm:var(--fdn-violet); --fm-soft:var(--fdn-violet-soft); }
.snx-formula.green { --fm:var(--fdn-green); --fm-soft:var(--fdn-green-soft); }
.snx-formula.amber { --fm:var(--fdn-orange); --fm-soft:var(--fdn-orange-soft); }
.snx-formula.red { --fm:var(--fdn-red); --fm-soft:var(--fdn-red-soft); }
.snx-formula small { display:inline-block; padding:4px 12px; border-radius:var(--fdn-radius-pill); font-size:12px;
  font-weight:800; letter-spacing:.3px; color:#fff; background:var(--fm); }
.snx-formula .eq { display:block; margin:14px 0 10px; padding:12px 10px; border-radius:var(--fdn-radius-sm);
  font-size:18px; color:var(--fdn-ink); background:var(--fdn-surface); overflow-x:auto; }
.snx-formula .eq math { font-size:20px; }
.snx-formula p { margin:0; font-size:14px; line-height:1.6; color:var(--fdn-body); }
.snx-formula:hover { transform:translateY(-5px); border-color:var(--fm); box-shadow:var(--fdn-shadow); }

/* notes + mistakes */
.snx-list { display:flex; flex-direction:column; gap:10px; }
.snx-note, .snx-mistake { display:flex; gap:12px; padding:14px 16px; border-radius:var(--fdn-radius);
  transition:transform .24s var(--fdn-ease); }
.snx-note > i, .snx-mistake > i { flex:0 0 auto; margin-top:3px; font-size:17px; }
.snx-note { background:var(--fdn-green-soft); border-left:5px solid var(--fdn-green); color:var(--fdn-ink); }
.snx-note > i { color:var(--fdn-green-dark); }
.snx-mistake { background:var(--fdn-red-soft); border-left:5px solid var(--fdn-red); color:var(--fdn-ink); }
.snx-mistake > i { color:var(--fdn-red-dark); }
.snx-note:hover, .snx-mistake:hover { transform:translateX(4px); }
.snx-note b, .snx-mistake b { display:block; margin-bottom:3px; font-size:15px; font-weight:800; }
.snx-note b { color:var(--fdn-green-dark); }
.snx-mistake b { color:var(--fdn-red-dark); }
.snx-note span, .snx-mistake span { display:block; max-width:72ch; font-size:15px; line-height:1.7; color:var(--fdn-body); }

/* prev / next - chunky buttons */
.snx-nav { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; margin-top:4px; }
.snx-navbtn { display:flex; align-items:center; gap:14px; padding:16px 18px; border-radius:var(--fdn-radius);
  background:var(--fdn-surface); border:2px solid var(--fdn-line); box-shadow:0 5px 0 var(--fdn-line-strong);
  cursor:pointer; text-align:left; transition:transform .12s ease, box-shadow .12s ease, border-color .2s ease; }
.snx-navbtn:focus { outline:none; }
.snx-navbtn:focus-visible { border-color:var(--snx-accent); }
.snx-navbtn:disabled { opacity:.5; cursor:not-allowed; box-shadow:none; }
.snx-navbtn:not(:disabled):hover { border-color:var(--snx-accent); transform:translateY(-2px);
  box-shadow:0 7px 0 var(--snx-accent-soft); }
.snx-navbtn:not(:disabled):active { transform:translateY(4px); box-shadow:0 1px 0 var(--fdn-line-strong); }
.snx-navbtn small { display:block; font-size:12px; font-weight:800; letter-spacing:.3px; text-transform:uppercase;
  color:var(--snx-accent-dark); }
.snx-navbtn strong { display:block; font-family:var(--fdn-display); font-size:16px; font-weight:600; line-height:1.3;
  color:var(--fdn-ink); }
.snx-navbtn .ic { display:flex; align-items:center; justify-content:center; width:44px; height:44px;
  border-radius:14px; font-size:16px; color:#fff; background:var(--snx-accent); box-shadow:0 4px 0 var(--snx-accent-dark);
  flex:0 0 auto; transition:transform .3s var(--fdn-bounce); }
.snx-navbtn:not(:disabled):hover .ic { transform:scale(1.08); }
.snx-navbtn:disabled .ic { background:var(--fdn-muted); box-shadow:none; }
.snx-navbtn.next { justify-content:flex-end; text-align:right; }

/* skeleton + states */
.snx-skel { display:grid; gap:16px; }
.snx-skel__card { padding:22px 24px; border-radius:var(--fdn-radius-lg); background:var(--fdn-surface);
  border:1.5px solid var(--fdn-line); }
.snx-skel__card .fdn-skel:last-child { margin-bottom:0; }
.snx-skel__card .fdn-skel:first-child { height:18px; margin-bottom:16px; }
.snx-side .fdn-empty { padding:24px 10px; }
.snx-side .fdn-empty__icon { width:56px; height:56px; font-size:22px; border-radius:18px;
  color:var(--snx-accent); background:var(--snx-accent-soft); }
.snx-main .fdn-empty__icon { color:var(--snx-accent); background:var(--snx-accent-soft); }
.snx-main .fdn-empty p, .snx-main .fdn-error p { max-width:46ch; margin:0 auto; font-size:15px; line-height:1.6; }
.snx-main .fdn-error p { color:var(--fdn-body); }
.snx-card.fdn-empty:hover, .snx-card.fdn-error:hover { transform:none; }

/* responsive */
@media (max-width:1199px){ .snx-points { grid-template-columns:repeat(2,minmax(0,1fr)); } }
@media (max-width:991px){
  .snx-grid { grid-template-columns:1fr; gap:16px; }
  .snx-side { position:static; max-height:none; }
}
@media (max-width:767px){
  .snx-filters { grid-template-columns:1fr; }
  .snx-points,.snx-formulae,.snx-nav { grid-template-columns:1fr; }
  .snx-hero { padding:20px 18px; }
  .snx-hero h2 { font-size:22px; }
  .snx-complete { width:100%; justify-content:center; }
  .snx-card { padding:18px 16px; }
  .snx-summary { padding:18px 16px; }
}
@media (prefers-reduced-motion:reduce){
  .snx *,.snx *:before,.snx *:after { animation-duration:.001s!important; transition-duration:.001s!important; }
}
`;

/* ========================================================================== */
/*  Presentational components — props only                                    */
/* ========================================================================== */

const Skeleton = ({ rows }) => (
  <div className="snx-skel">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="snx-skel__card" key={index}>
        <span className="fdn-skel" style={{ width: "40%" }} />
        <span className="fdn-skel" style={{ width: "85%" }} />
        <span className="fdn-skel" style={{ width: "65%" }} />
      </div>
    ))}
  </div>
);

const SectionCard = ({ icon, gradient, title, subtitle, children }) => (
  <section className="snx-card">
    <header className="snx-card__head">
      <span className="ic" style={{ background: gradient }}>
        <i className={icon} />
      </span>
      <div>
        <h5>{title}</h5>
        {subtitle ? <small>{subtitle}</small> : null}
      </div>
    </header>
    {children}
  </section>
);

const Filters = ({ search, subject, subjects, onChange }) => (
  <div className="snx-filters">
    <div className="snx-field">
      <i className="fas fa-search" />
      <input
        type="text"
        value={search}
        placeholder="Search a chapter or topic…"
        onChange={(event) => onChange("search", event.target.value)}
      />
    </div>

    <div className={`snx-field snx-field--subject ${subjectClass(subject)}`}>
      <span className="snx-field__badge">
        <i className={SUBJECT_ICON[subject] || "fas fa-book"} />
      </span>
      <i className="fas fa-chevron-down snx-field__caret" />
      <select
        value={subject}
        aria-label="Subject"
        onChange={(event) => onChange("subject", event.target.value)}
      >
        {(subjects && subjects.length ? subjects : SUBJECT_OPTIONS).map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
    {/* class picker removed — class comes from the logged-in student */}
  </div>
);

const Header = ({ subject, classValue, stats, search, subjects, onChange }) => (
  <header className="fdn-hero fdn-hero--pink snx-head">
    <div className="fdn-hero__row">
      <span className="fdn-hero__icon">
        <i className="fas fa-book-open" />
      </span>
      <div>
        <h1 className="fdn-hero__title">IIT Foundation Short Notes</h1>
        <p className="fdn-hero__sub">
          {subject} • Class {classValue} — quick revision for IIT Foundation, Olympiad, NTSE &amp; NEET
          Foundation
        </p>
      </div>

      <div className="fdn-hero__stats">
        {stats.map((stat) => (
          <div className="fdn-hero__stat" key={stat.id}>
            <strong>{stat.value}</strong>
            <small>{stat.label}</small>
          </div>
        ))}
      </div>
    </div>

    <div className="fdn-hero__body">
      <Filters search={search} subject={subject} subjects={subjects} onChange={onChange} />
    </div>
  </header>
);

const Sidebar = ({ chapters, activeTopicId, openChapters, onToggleChapter, onSelectTopic }) => (
  <aside className="snx-side">
    <h6 className="snx-side__title">
      <i className="fas fa-layer-group" />
      Chapters &amp; Topics
    </h6>

    {chapters.length === 0 ? (
      <div className="fdn-empty">
        <div className="fdn-empty__icon">
          <i className="fas fa-search" />
        </div>
        Nothing to show here.
      </div>
    ) : null}

    {chapters.map((chapter) => {
      const isOpen = openChapters.indexOf(chapter.id) !== -1;
      return (
        <div className={`snx-chapterbox ${isOpen ? "is-open" : ""}`} key={chapter.id}>
          <button type="button" className="snx-chapterbox__btn" onClick={() => onToggleChapter(chapter.id)}>
            <span className="ic">
              <i className="fas fa-book" />
            </span>
            {chapter.chapterName}
            <i className={`fas fa-chevron-${isOpen ? "up" : "down"} caret`} />
          </button>

          {isOpen ? (
            <div className="snx-topics">
              {(chapter.topics || []).map((topic) => (
                <button
                  type="button"
                  key={topic.id}
                  className={`snx-topic ${String(activeTopicId) === String(topic.id) ? "is-active" : ""}`}
                  onClick={() => onSelectTopic(chapter.id, topic.id)}
                >
                  <span className="dot" style={{ background: DOT_COLOR[difficultyTone(topic.difficulty)] }} />
                  {topic.name}
                  <small>{topic.readingTime}</small>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      );
    })}
  </aside>
);

const TopicHero = ({ chapter, topic, subject, classValue, completed, onComplete }) => (
  <div className={`snx-hero ${subjectClass(subject)}`}>
    <div className="snx-hero__crumb">
      {subject} &nbsp;›&nbsp; Class {classValue} &nbsp;›&nbsp; {chapter.chapterName}
    </div>
    <div className="snx-hero__title">
      <h2>{topic.name}</h2>
      <button
        type="button"
        className={`snx-complete ${completed ? "is-done" : ""}`}
        onClick={onComplete}
        disabled={completed}
      >
        <i className={`fas ${completed ? "fa-check-circle" : "fa-circle"}`} />
        {completed ? "Completed" : "Mark as complete"}
      </button>
    </div>

    <div className="snx-hero__meta">
      {topic.readingTime ? (
        <span className="snx-meta">
          <i className="fas fa-clock" />
          {topic.readingTime} read
        </span>
      ) : null}
      {topic.difficulty ? (
        <span className={`snx-badge ${difficultyTone(topic.difficulty)}`}>
          <i className="fas fa-signal" />
          {topic.difficulty}
        </span>
      ) : null}
      <span className="snx-meta">
        <i className="fas fa-key" />
        {(topic.keyPoints || []).length} key points
      </span>
      <span className="snx-meta">
        <i className="fas fa-square-root-alt" />
        {(topic.formulae || []).length} formulae
      </span>
    </div>
  </div>
);

const QuickSummary = ({ text }) =>
  !text ? null : (
    <div className="snx-summary">
      <h5>
        <i className="fas fa-bolt" />
        Quick Summary
      </h5>
      <p>{text}</p>
    </div>
  );

const KeyPoints = ({ points }) =>
  !points || !points.length ? null : (
    <SectionCard
      icon="fas fa-key"
      gradient="linear-gradient(140deg,var(--fdn-blue),var(--fdn-blue-dark))"
      title="Key Points"
      subtitle="The must-know ideas"
    >
      <div className="snx-points">
        {points.map((point) => (
          <div className="snx-point" key={point.id}>
            <span className="ic">
              <i className={iconFor(point.icon)} />
            </span>
            <h6>{point.title}</h6>
            <p>{point.text}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );

/* Formulae arrive as MathML strings, so MathJax typesets them. */
const Formulae = ({ formulae }) =>
  !formulae || !formulae.length ? null : (
    <SectionCard
      icon="fas fa-square-root-alt"
      gradient="linear-gradient(140deg,var(--fdn-violet),var(--fdn-violet-dark))"
      title="Important Formulae"
      subtitle="Learn these by heart"
    >
      <div className="snx-formulae">
        {formulae.map((item) => (
          <div className={`snx-formula ${formulaTone(item.tone)}`} key={item.id}>
            <small>{item.title}</small>
            <MathJax dynamic>
              <span className="eq" dangerouslySetInnerHTML={{ __html: item.formula }} />
            </MathJax>
            {item.note ? <p>{item.note}</p> : null}
          </div>
        ))}
      </div>
    </SectionCard>
  );

const ImportantNotes = ({ notes }) =>
  !notes || !notes.length ? null : (
    <SectionCard
      icon="fas fa-sticky-note"
      gradient="linear-gradient(140deg,var(--fdn-green),var(--fdn-green-dark))"
      title="Important Notes"
      subtitle="Do not miss these"
    >
      <div className="snx-list">
        {notes.map((note) => (
          <div className="snx-note" key={note.id}>
            <i className="fas fa-check-circle" />
            <div>
              {note.title ? <b>{note.title}</b> : null}
              <span>{note.text}</span>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );

const CommonMistakes = ({ mistakes }) =>
  !mistakes || !mistakes.length ? null : (
    <SectionCard
      icon="fas fa-exclamation-triangle"
      gradient="linear-gradient(140deg,var(--fdn-red),var(--fdn-red-dark))"
      title="Common Mistakes"
      subtitle="Avoid these in the exam"
    >
      <div className="snx-list">
        {mistakes.map((mistake) => (
          <div className="snx-mistake" key={mistake.id}>
            <i className="fas fa-times-circle" />
            <div>
              {mistake.title ? <b>{mistake.title}</b> : null}
              <span>{mistake.text}</span>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );

const TopicNav = ({ previous, next, onSelect }) => (
  <div className="snx-nav">
    <button type="button" className="snx-navbtn" disabled={!previous} onClick={() => previous && onSelect(previous)}>
      <span className="ic">
        <i className="fas fa-arrow-left" />
      </span>
      <span>
        <small>Previous topic</small>
        <strong>{previous ? previous.topic.name : "You are at the start"}</strong>
      </span>
    </button>

    <button type="button" className="snx-navbtn next" disabled={!next} onClick={() => next && onSelect(next)}>
      <span>
        <small>Next topic</small>
        <strong>{next ? next.topic.name : "You are at the end"}</strong>
      </span>
      <span className="ic">
        <i className="fas fa-arrow-right" />
      </span>
    </button>
  </div>
);

/* ========================================================================== */
/*  View — receives the query result through props                            */
/* ========================================================================== */

class ShortNotesView extends Component {
  constructor(props) {
    super(props);
    this.state = { openChapters: [], activeTopicId: null, completedIds: {} };
    // per-topic reading-time tracking
    this.trackedTopicId = null;
    this.trackedChapterId = null;
    this.trackedStartedAt = null;
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleUnload);
    this.syncTracking();
  }

  componentDidUpdate() {
    this.syncTracking();
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleUnload);
    this.flush(false);
  }

  handleUnload = () => this.flush(false);

  /** When the visible topic changes, bank the time spent on the previous one. */
  syncTracking = () => {
    const entry = this.activeEntry();
    const topicId = entry ? entry.topic.id : null;
    if (String(topicId) === String(this.trackedTopicId)) return;
    this.flush(false); // bank the previous topic before switching
    this.trackedTopicId = topicId;
    this.trackedChapterId = entry ? entry.chapter.id : null;
    this.trackedStartedAt = topicId != null ? Date.now() : null;
  };

  /** Persist the current topic's elapsed time (and completion) to the server. */
  flush = (completed) => {
    const rid = this.props.registrationId;
    const topicId = this.trackedTopicId;
    if (!rid || topicId == null || !this.trackedStartedAt) return;
    const seconds = Math.max(0, Math.round((Date.now() - this.trackedStartedAt) / 1000));
    this.trackedStartedAt = Date.now(); // reset the accumulator
    if (!completed && seconds < 2) return; // ignore incidental glances
    if (!this.props.client) return;
    this.props.client
      .mutate({
        mutation: SAVE_NOTES_PROGRESS,
        variables: {
          registration_id: Number(rid),
          topic_id: Number(topicId),
          chapter_id: this.trackedChapterId != null ? Number(this.trackedChapterId) : null,
          seconds,
          completed: !!completed,
        },
      })
      .catch(() => {});
  };

  markComplete = () => {
    const topicId = this.trackedTopicId;
    if (topicId == null) return;
    this.setState((s) => ({ completedIds: { ...s.completedIds, [topicId]: true } }));
    this.flush(true);
  };

  chapters = () => {
    const result = this.props.notes || {};
    return result.getIITFoundationShortNotes || [];
  };

  /** Client-side search across chapter and topic names. */
  visibleChapters = () => {
    const query = String(this.props.search || "").trim().toLowerCase();
    if (!query) return this.chapters();

    return this.chapters()
      .map((chapter) => ({
        ...chapter,
        topics: (chapter.topics || []).filter(
          (topic) =>
            String(topic.name).toLowerCase().indexOf(query) !== -1 ||
            String(chapter.chapterName).toLowerCase().indexOf(query) !== -1
        ),
      }))
      .filter((chapter) => chapter.topics.length > 0);
  };

  flatTopics = () => {
    const flat = [];
    this.visibleChapters().forEach((chapter) => {
      (chapter.topics || []).forEach((topic) => flat.push({ chapter, topic }));
    });
    return flat;
  };

  activeEntry = () => {
    const flat = this.flatTopics();
    return (
      flat.find((entry) => String(entry.topic.id) === String(this.state.activeTopicId)) || flat[0] || null
    );
  };

  toggleChapter = (id) =>
    this.setState((state) => ({
      openChapters:
        state.openChapters.indexOf(id) !== -1
          ? state.openChapters.filter((item) => item !== id)
          : [...state.openChapters, id],
    }));

  selectTopic = (chapterId, topicId) => {
    this.setState((state) => ({
      activeTopicId: topicId,
      openChapters:
        state.openChapters.indexOf(chapterId) !== -1 ? state.openChapters : [...state.openChapters, chapterId],
    }));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  render() {
    const result = this.props.notes || {};
    const loading = result.loading;
    const error = result.error;

    const chapters = this.visibleChapters();
    const flat = this.flatTopics();
    const entry = this.activeEntry();

    // the first chapter opens by itself once data arrives
    const openChapters =
      this.state.openChapters.length || !chapters.length ? this.state.openChapters : [chapters[0].id];

    const index = entry ? flat.findIndex((item) => String(item.topic.id) === String(entry.topic.id)) : -1;
    const previous = index > 0 ? flat[index - 1] : null;
    const next = index > -1 && index < flat.length - 1 ? flat[index + 1] : null;

    const stats = [
      { id: "chapters", value: chapters.length, label: "Chapters" },
      { id: "topics", value: flat.length, label: "Topics" },
    ];

    // Captions read "Class 8". When the page was opened by class_id alone the
    // numeral comes back on the chapters themselves, so borrow it from there.
    const classLabel =
      this.props.classValue || (chapters.length ? chapters[0].class : "") || "";

    return (
      <div className="snx">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />

        <Header
          subject={this.props.subject}
          classValue={classLabel}
          stats={stats}
          search={this.props.search}
          subjects={this.props.subjects}
          onChange={this.props.onFilterChange}
        />

        <div className="snx-grid">
          <Sidebar
            chapters={chapters}
            activeTopicId={entry ? entry.topic.id : null}
            openChapters={openChapters}
            onToggleChapter={this.toggleChapter}
            onSelectTopic={this.selectTopic}
          />

          <main className="snx-main">
            {loading && !chapters.length ? (
              <Skeleton rows={4} />
            ) : error ? (
              <div className="snx-card fdn-error">
                <div className="fdn-error__icon">
                  <i className="fas fa-exclamation-triangle" />
                </div>
                <h4>Oops, something went wrong</h4>
                <p>Could not load the notes. Please check your connection and try again.</p>
              </div>
            ) : !entry ? (
              <div className="snx-card fdn-empty">
                <div className="fdn-empty__icon">
                  <i className="fas fa-book" />
                </div>
                <h4>No notes yet</h4>
                <p>
                  No notes published for {this.props.subject}, Class {classLabel} yet.
                </p>
              </div>
            ) : (
              <React.Fragment>
                <TopicHero
                  chapter={entry.chapter}
                  topic={entry.topic}
                  subject={this.props.subject}
                  classValue={classLabel}
                  completed={!!this.state.completedIds[entry.topic.id]}
                  onComplete={this.markComplete}
                />

                <QuickSummary text={entry.topic.quickSummary} />
                <KeyPoints points={entry.topic.keyPoints} />
                <Formulae formulae={entry.topic.formulae} />
                <ImportantNotes notes={entry.topic.importantNotes} />
                <CommonMistakes mistakes={entry.topic.commonMistakes} />

                <TopicNav
                  previous={previous}
                  next={next}
                  onSelect={(target) => this.selectTopic(target.chapter.id, target.topic.id)}
                />
              </React.Fragment>
            )}
          </main>
        </div>
      </div>
    );
  }
}

/* Query wrapper: variables come from props, so changing a filter refetches.
   class_id wins when the student's session supplied one; class is the fallback
   for a visitor with no session, and the two are mutually exclusive so the
   server never has to reconcile them. */
const ShortNotesData = compose(
  graphql(GET_IIT_FOUNDATION_SHORT_NOTES, {
    options: (props) => {
      const classId =
        props.classId === null || props.classId === undefined || props.classId === ""
          ? null
          : Number(props.classId);

      return {
        variables: {
          subject: props.subject,
          class_id: classId,
          class: classId ? null : props.classValue,
        },
        fetchPolicy: "cache-and-network",
      };
    },
    name: "notes",
  }),
  withApollo // provides `client` for the progress-tracking mutation
)(ShortNotesView);

/* ========================================================================== */
/*  Page — owns the filters and the MathJax context                           */
/* ========================================================================== */

class ShortNotesPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subject: props.subject || (props.subjects && props.subjects[0]) || SUBJECT_OPTIONS[0],
      // classId identifies the chapters; classValue only labels them. With a
      // session present classId is set and classValue is just the caption; with
      // no session classId is null and classValue drives the legacy lookup.
      classId: props.classId || null,
      classValue: props.classValue || (props.classId ? "" : "10"),
      search: "",
    };
  }

  handleFilterChange = (key, value) => this.setState({ [key]: value });

  render() {
    return (
      <MathJaxContext version={3} config={MATHJAX_CONFIG}>
        <ShortNotesData
          subject={this.state.subject}
          subjects={this.props.subjects}
          classId={this.state.classId}
          classValue={this.state.classValue}
          registrationId={this.props.registrationId}
          search={this.state.search}
          onFilterChange={this.handleFilterChange}
        />
      </MathJaxContext>
    );
  }
}

export { ShortNotesView, ShortNotesData };
export default ShortNotesPage;
